import { useDispatch } from "react-redux";

import styles from "./MobileComponents.module.css";
import { setItemId } from "../../../../store/slices/currentItem.slice";
import { useTranslation } from "react-i18next";
import { useGetVideoDetailsQuery } from "../../../../store/api/filmAPI";
import useLikeClick from "../../../../hooks/useLikeClick";
import useFavouriteClick from "../../../../hooks/useFavouriteClick";
import { formatLikeCount, languageHandler, movieTitleHandler, posterPathHandler } from "../../../../utils/helpers";
import { genresData } from "../../../../constants/structure";
import { setCountFeed } from "../../../../store/slices/feed.slice";
import { setLoginModal } from "../../../../store/slices/modals.slice";
import { MainLike } from "../../../../components/icons/reactionMenu/MainLike";
import Comments from "../../../../components/icons/reactionMenu/Comments";
import Saved from "../../../../components/icons/reactionMenu/Saved";
import OverlayRatingIcon from "../../../../components/icons/searchIcons/OverlayRatingIcon";
import OverlayTomatoRatingIcon from "../../../../components/icons/searchIcons/OverlayTomatoRatingIcon";

interface IProps {
  voteCount: number;
  isMobile: boolean;
  currentItemId: number;
  clickHandler: () => void;
  item: any;
}
const MobileBottomInfoBlock = ({ voteCount, isMobile, currentItemId, clickHandler, item }: IProps) => {
  const dispatch = useDispatch();

  const { t, i18n } = useTranslation();
  const { likesList, likeHandler } = useLikeClick();
  const { favoritesList, favouritesHandler } = useFavouriteClick();

  const { data, isLoading } = useGetVideoDetailsQuery({
    queries: { language: languageHandler(i18n.language) },
    id: currentItemId,
  });

  if (isLoading) {
    return <></>;
  }

  const title = movieTitleHandler(i18n.language, item);
  const posterPath = posterPathHandler(i18n.language, item);
  const year = item.release_date?.substring(0, 4);
  const genres = (item.genre_ids as number[])
    .slice(0, 2)
    .map((genreId) => genresData.find((genre) => genre.id === genreId))
    .filter(Boolean);
  const liked = likesList.data?.results.includes(data?.id);
  const inFavourites = favoritesList.data?.results.includes(data?.id);
  const primaryRating = item.imdb_rating ?? item.vote_average;
  const secondaryRating = item.rotten_tomatoes_rating ?? item.imdb_rating ?? item.vote_average;
  const commentsCount = item.reviews_count ?? item.comments_count ?? item.shares_count ?? 0;

  const formatCount = (value?: number) => {
    const count = value || 0;
    if (count >= 1000) return `${Math.round(count / 1000)}K`;
    return count;
  };

  const updateLocalCount = (id: any, key: any, type: any) => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;

    dispatch(setCountFeed({ id, key, type }));
  };

  const handleLike = (event: any) => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      dispatch(setLoginModal(true));
      return;
    }

    updateLocalCount(item.id, "vote_count", liked ? "decrement" : "increment");
    likeHandler(event, item, liked);
  };

  const handleSaved = (event: any) => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      dispatch(setLoginModal(true));
      return;
    }

    updateLocalCount(item.id, "favorites_count", inFavourites ? "decrement" : "increment");
    favouritesHandler(event, item, inFavourites);
  };

  return (
    <div className={styles.mobileBottomMenu}>
      <button
        type="button"
        className={styles.mobileMovieCard}
        onClick={() => {
          clickHandler();
          dispatch(setItemId(currentItemId));
        }}
      >
        {posterPath && (
          <img
            className={styles.mobileMoviePoster}
            src={`${process.env.REACT_APP_IMG_BASE_URL}${posterPath}`}
            alt={title}
            draggable={false}
          />
        )}
        <span className={styles.mobileMovieInfo}>
          <span className={styles.mobileMovieTitle}>{title}</span>
          <span className={styles.mobileMovieMeta}>
            {genres.map((genre: any) => (
              <span key={genre.id}>{t(genre.name)}</span>
            ))}
            {year && <span className={styles.mobileMovieYear}>{year}</span>}
          </span>
          <span className={styles.mobileRatings}>
            {primaryRating != null && (
              <span className={styles.mobileRating}>
                <OverlayRatingIcon />
                {primaryRating.toFixed(1)}
              </span>
            )}
            {secondaryRating != null && (
              <span className={styles.mobileRating}>
                <OverlayTomatoRatingIcon />
                {secondaryRating.toFixed(1)}
              </span>
            )}
          </span>
        </span>
      </button>
      <div className={styles.mobileActions}>
        <button type="button" className={styles.mobileActionButton} onClick={handleLike}>
          <MainLike isMobile={isMobile} liked={liked} />
          <span>{formatLikeCount(voteCount, { compact: true, suffix: "K" })}</span>
        </button>
        <button type="button" className={styles.mobileActionButton} onClick={clickHandler}>
          <Comments isMobile={isMobile} />
          <span>{formatCount(commentsCount)}</span>
        </button>
        <button type="button" className={styles.mobileActionButton} onClick={handleSaved}>
          <Saved isMobile={isMobile} inFavourites={inFavourites} />
          <span>{formatCount(item.favorites_count)}</span>
        </button>
      </div>
    </div>
  );
};

export default MobileBottomInfoBlock;
